// components/widgets/AgentDashboardPanel.tsx
// Agent-specific dashboard with charts and metrics for the right sidebar
import React, { useState, useMemo, useEffect } from 'react';
import {
  PieChart, Pie, BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, Legend
} from 'recharts';
import { TestResult, AgentProfile } from '../../types';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface Defect {
  id: string;
  defect_id_str: string;
  test_name: string;
  agent_name: string;
  test_type: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  status: 'New' | 'Acknowledged' | 'In Progress' | 'Resolved' | 'Closed';
  task_prompt: string;
  ai_analysis: string;
  failure_reason: string;
  created_at: string;
  updated_at: string;
}

interface AgentDashboardPanelProps {
  selectedAgent: string;
  currentAgent?: AgentProfile;
  results: Map<string, TestResult>;
  defects: any[]; // Keep this for compatibility but we'll fetch our own
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  aiConfig?: any;
  lastDataRefresh?: number;
  currentScope?: 'agent' | 'api' | 'application';
  selectedAPI?: string;
  selectedApplication?: string;
  apis?: any[];
  applications?: any[];
}

const AgentDashboardPanel: React.FC<AgentDashboardPanelProps> = ({
  selectedAgent,
  currentAgent,
  results,
  defects, // Keep for compatibility but fetch our own
  isCollapsed,
  onToggleCollapse,
  aiConfig,
  lastDataRefresh,
  currentScope = 'agent',
  selectedAPI,
  selectedApplication,
  apis = [],
  applications = []
}) => {


  const selectedEntity = (() => {
    if (currentScope === 'api') {
      return selectedAPI && selectedAPI !== 'all' ? selectedAPI : '';
    } else if (currentScope === 'application') {
      return selectedApplication && selectedApplication !== 'all' ? selectedApplication : '';
    } else {
      return selectedAgent && selectedAgent !== 'all' ? selectedAgent : '';
    }
  })();

  const entityType = currentScope || 'agent';


  // ADD THIS: Filter results based on selected entity
  const filteredResults = React.useMemo(() => {
    if (!selectedEntity) return new Map();

    // For now, return empty results for API/Application until we have proper test result filtering
    if (currentScope === 'api' || currentScope === 'application') {
      return new Map(); // TODO: Filter results by API/Application when backend supports it
    }

    // For agents, return the existing results
    return results;
  }, [results, selectedEntity, currentScope]);

  //console.log('[Debug] Dashboard received aiConfig:', aiConfig);  // ADD THIS LINE
  //console.log('[Debug] Dashboard received currentAgent:', currentAgent);
  // Add token usage and cost state
  const [tokenUsage, setTokenUsage] = useState({
    test_generation: 0,
    execution: 0,
    validation: 0,
    defect_analysis: 0,
    total: 0
  });

  const [costData, setCostData] = useState({
    test_generation: 0,
    execution: 0,
    validation: 0,
    defect_analysis: 0,
    total: 0
  });


  // Fetch token usage when agent changes
  useEffect(() => {
    const fetchTokenUsage = async () => {
      if (!selectedEntity) {
        // Reset to default when no agent selected
        setTokenUsage({
          test_generation: 0,
          execution: 0,
          validation: 0,
          defect_analysis: 0,
          total: 0
        });
        setCostData({
          test_generation: 0,
          execution: 0,
          validation: 0,
          defect_analysis: 0,
          total: 0
        });
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/token-usage/${selectedEntity}`);
        //const response = await fetch(`${API_BASE_URL}/token-usage/${selectedAgent}`);
        if (response.ok) {
          const data = await response.json();
          //console.log('Token usage API response:', data); // Debug log

          // Check for backend error
          if (data.error) {
            console.error('Backend token API error:', data.error);
            setTokenUsage({
              test_generation: 0,
              execution: 0,
              validation: 0,
              defect_analysis: 0,
              total: 0
            });
            return;
          }

          // Handle different API response formats
          if (data.interactions && data.totals) {
            // New API format: {interactions: {test_generation: {tokens, cost}, ...}, totals: {...}}
            const interactions = data.interactions;
            setTokenUsage({
              test_generation: interactions.test_generation?.tokens || 0,
              execution: interactions.execution?.tokens || 0,
              validation: interactions.validation?.tokens || 0,
              defect_analysis: interactions.defect_analysis?.tokens || 0,
              total: data.totals?.total_tokens || 0
            });
            setCostData({
              test_generation: interactions.test_generation?.cost || 0,
              execution: interactions.execution?.cost || 0,
              validation: interactions.validation?.cost || 0,
              defect_analysis: interactions.defect_analysis?.cost || 0,
              total: data.totals?.total_cost || 0
            });
          } else if (data.tokens && data.costs) {
            // Alternative new format: {tokens: {...}, costs: {...}}
            setTokenUsage(data.tokens);
            setCostData(data.costs);
          } else {
            // Fallback: Legacy API format
            setTokenUsage({
              test_generation: data.test_generation || 0,
              execution: data.execution || 0,
              validation: data.validation || 0,
              defect_analysis: data.defect_analysis || 0,
              total: data.total || 0
            });
            // Set default costs for legacy format
            setCostData({
              test_generation: 0,
              execution: 0,
              validation: 0,
              defect_analysis: 0,
              total: 0
            });
          }
        } else {
          console.warn('Token usage API returned:', response.status);
          // Keep current state on API error
        }
      } catch (error) {
        console.error('Failed to fetch token usage:', error);
        // Keep current state on network error
      }
    };

    fetchTokenUsage();
  }, [selectedEntity, lastDataRefresh]);

  // Calculate test execution metrics
  const executionMetrics = useMemo(() => {
    const resultsArray = Array.from(filteredResults.values());
    const total = resultsArray.length;
    const passed = resultsArray.filter(r => r.status === 'PASSED').length;
    const failed = resultsArray.filter(r => r.status === 'FAILED').length;
    const errors = resultsArray.filter(r => r.status === 'ERROR').length;
    const successRate = total > 0 ? (passed / total) * 100 : 0;
    const avgConfidence = resultsArray.length > 0
      ? resultsArray.reduce((sum, r) => sum + (r.ai_confidence || 0), 0) / resultsArray.length * 100
      : 0;

    return {
      total,
      passed,
      failed,
      errors,
      successRate,
      avgConfidence
    };
  }, [filteredResults]);

  // Calculate defect metrics using the real defects data
  const defectMetrics = useMemo(() => {
    const total = defects.length;
    const statusCounts = defects.reduce((acc, defect) => {
      acc[defect.status] = (acc[defect.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const severityCounts = defects.reduce((acc, defect) => {

      acc[defect.severity] = (acc[defect.severity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const critical = severityCounts['Critical'] || 0;
    const high = severityCounts['High'] || 0;
    const open = statusCounts['New'] || 0;
    const inProgress = (statusCounts['Acknowledged'] || 0) + (statusCounts['In Progress'] || 0);
    const resolved = statusCounts['Resolved'] || 0;
    const closed = statusCounts['Closed'] || 0;

    return {
      total,
      critical,
      high,
      open,
      inProgress,
      resolved,
      closed,
      resolutionRate: total > 0 ? ((resolved + closed) / total) * 100 : 0
    };
  }, [defects]);

  // Prepare chart data
  const pieData = [
    { name: 'Passed', value: executionMetrics.passed, color: '#28a745' },
    { name: 'Failed', value: executionMetrics.failed, color: '#dc3545' },
    { name: 'Errors', value: executionMetrics.errors, color: '#ffc107' }
  ].filter(item => item.value > 0);

  const defectPieData = [
    { name: 'New', value: defectMetrics.open, color: '#dc3545' },
    { name: 'In Progress', value: defectMetrics.inProgress, color: '#ffc107' },
    { name: 'Resolved', value: defectMetrics.resolved, color: '#28a745' },
    { name: 'Closed', value: defectMetrics.closed, color: '#6c757d' }
  ].filter(item => item.value > 0);

  // Get success rate color
  const getSuccessRateColor = (rate: number) => {
    if (rate >= 80) return '#28a745';
    if (rate >= 60) return '#ffc107';
    return '#dc3545';
  };

  // Get quality score based on pass rate (not defect density)
  const getQualityScore = () => {
    if (executionMetrics.total === 0) return { score: 'N/A', color: '#6c757d' };
    const passRate = executionMetrics.successRate;
    if (passRate >= 95) return { score: 'A+', color: '#28a745' };
    if (passRate >= 85) return { score: 'A', color: '#20c997' };
    if (passRate >= 75) return { score: 'B+', color: '#ffc107' };
    if (passRate >= 65) return { score: 'B', color: '#fd7e14' };
    if (passRate >= 50) return { score: 'C', color: '#ff6b35' };
    return { score: 'F', color: '#dc3545' };
  };

  const qualityScore = getQualityScore();

  if (isCollapsed) {
    return (
      <aside className="dashboard-panel collapsed" aria-label="Agent Dashboard Panel">
        <div className="dashboard-panel-inner">
          <div className="dashboard-toolbar">
            <button
              className="pushable push-secondary btn-xs"
              onClick={onToggleCollapse}
              aria-expanded={false}
              title="Open dashboard"
            >
              <span className="shadow"></span><span className="edge"></span>
              <span className="front">▸ Dashboard</span>
            </button>
          </div>
        </div>
      </aside>
    );
  }

  // Add debugging logs here, BEFORE the return

  /*
  console.log('IN Test Execution Overview :: selectedEntity :: ', selectedEntity);
  console.log('IN Test Execution Overview :: currentScope :: ', currentScope);
  console.log('IN Test Execution Overview :: results :: ', results);
  console.log('IN Test Execution Overview :: defects :: ', defects);
  console.log('IN Test Execution Overview :: selectedApplication :: ', selectedApplication);
  console.log('IN Test Execution Overview :: applications :: ', applications);
*/

  return (
    <>
      {/* Column 2: Test Execution Overview */}
      <div className="dashboard-column" aria-label="Test Execution Column">
        {/* {(!selectedEntity || (results.size === 0 && defects.length === 0)) ? ( */}
        {(!selectedEntity) ? (
          <div className="dashboard-placeholder">
            <p>Select {entityType === 'api' ? 'AN API' : entityType === 'application' ? 'AN APPLICATION' : 'AN AGENT'} to view metrics</p>
            {selectedEntity && (
              <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                No test execution data available yet. Generate and run tests to see metrics.
              </p>
            )}
          </div>
        ) : (
          <section className="dashboard-ribbon">
            <div className="ribbon-header">
              <h4>Test Execution Overview</h4>
            </div>
            <div className="ribbon-content">
              <div style={{ display: 'flex', gap: '2rem', height: 'auto' }}>
                {/* Test Execution Chart and Metrics */}
                <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                  <div className="chart-section" style={{ height: '180px' }}>
                    {pieData && pieData.length > 0 && pieData.some(item => item.value > 0) ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={20}
                            outerRadius={70}
                            paddingAngle={1}
                            dataKey="value"
                            isAnimationActive={false}
                            label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                              const radius = innerRadius + (outerRadius - innerRadius) * 0.3;
                              const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                              const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

                              return (
                                <text
                                  x={x}
                                  y={y}
                                  fill="white"
                                  textAnchor={x > cx ? 'start' : 'end'}
                                  dominantBaseline="central"
                                  fontSize="12"
                                  fontWeight="bold"
                                >
                                  {`${value}`}
                                </text>
                              );
                            }}
                            labelLine={false}
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{
                        height: '180px',
                        width: '200px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#189102ff',
                        fontSize: '0.9rem'
                      }}>
                        No tests executed
                      </div>
                    )}
                  </div> {/* chart-section */}
                  <div className="metrics-section">
                    <div className="metric-row">
                      <span className="metric-label">Success Rate: </span>
                      <span
                        className="metric-value"
                        style={{ color: getSuccessRateColor(executionMetrics.successRate), fontWeight: 'normal' }}
                      >
                        {executionMetrics.successRate.toFixed(1)}%
                      </span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Total: </span>
                      <span className="metric-value" style={{ fontWeight: 'normal' }}>{executionMetrics.total}</span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Passed: </span>
                      <span className="metric-value" style={{ color: '#28a745', fontWeight: 'normal' }}>
                        {executionMetrics.passed}
                      </span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Failed: </span>
                      <span className="metric-value" style={{ color: '#dc3545', fontWeight: 'normal' }}>
                        {executionMetrics.failed}
                      </span>
                    </div>
                  </div> {/* metrics-section */}
                </div> {/* FLEX 1 */}

                {/* Defect Analysis Chart and Metrics */}
                <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                  <div className="chart-section" style={{ height: '180px' }}>
                    {defectPieData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={defectPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={1}
                            dataKey="value"
                            isAnimationActive={false}
                            label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                              const radius = innerRadius + (outerRadius - innerRadius) * 0.3;
                              const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                              const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

                              return (
                                <text
                                  x={x}
                                  y={y}
                                  fill="white"
                                  textAnchor={x > cx ? 'start' : 'end'}
                                  dominantBaseline="central"
                                  fontSize="12"
                                  fontWeight="bold"
                                >
                                  {`${value}`}
                                </text>
                              );
                            }}
                            labelLine={false}
                          >
                            {defectPieData.map((entry, index) => (
                              <Cell key={`defect-cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{
                        height: '180px',
                        width: '200px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#189102ff',
                        fontSize: '0.9rem'
                      }}>
                        No defects
                      </div>
                    )}
                  </div>
                  <div className="metrics-section">
                    <div className="metric-row">
                      <span className="metric-label">Quality Score: </span>
                      <span
                        className="metric-value"
                        style={{ color: qualityScore.color, fontWeight: 'normal' }}
                      >
                        {qualityScore.score}
                      </span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Total Defects: </span>
                      <span className="metric-value" style={{ fontWeight: 'normal' }}>{defectMetrics.total}</span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Critical: </span>
                      <span className="metric-value" style={{ color: '#dc3545', fontWeight: 'normal' }}>
                        {defectMetrics.critical}
                      </span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Resolution Rate: </span>
                      <span className="metric-value" style={{ color: '#28a745', fontWeight: 'normal' }}>
                        {defectMetrics.resolutionRate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Column 3: AI Usage + AI Performance stacked vertically */}
      <div className="dashboard-column" aria-label="AI Metrics Column">
        {!selectedEntity ? (
          <div className="dashboard-placeholder">
            <p>Select {entityType === 'api' ? 'AN API' : entityType === 'application' ? 'AN APPLICATION' : 'AN AGENT'} to view AI metrics</p>
          </div>
        ) : (
          <>
            {/* AI Model Statistics */}
            <section className="dashboard-ribbon">
              <div className="ribbon-header">
                <h4>AI Model Statistics</h4>
              </div>
              <div className="ribbon-content">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.4rem' }}>
                  {/* Model Badge */}
                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>Model Being Used</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold' }}>
                      {aiConfig?.default_model || 'Not Configured'}
                    </div>
                  </div>

                  {/* AI Confidence */}
                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>AI Confidence</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold', color: executionMetrics.avgConfidence >= 80 ? '#28a745' : executionMetrics.avgConfidence >= 60 ? '#ffc107' : '#dc3545' }}>
                      {executionMetrics.avgConfidence.toFixed(1)}%
                    </div>
                  </div>

                  {/* Agent Domain */}
                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>
                      {currentScope === 'api' ? 'API Domain' : 'Agent Domain'}
                    </div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold' }}>
                      {(() => {
                        if (currentScope === 'api' && selectedAPI) {
                          const api = apis?.find(a => a.name === selectedAPI);
                          return api?.domain || 'general';
                        }
                        return currentAgent?.domain || 'N/A';
                      })()}
                    </div>
                  </div>

                  {/* Token Badges - All same color */}
                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>Test Generation</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold' }}>{(tokenUsage.test_generation || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '0.6rem', opacity: 0.6 }}>tokens | ${(costData.test_generation || 0).toFixed(3)}</div>
                  </div>

                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>Execution</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold' }}>{(tokenUsage.execution || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '0.6rem', opacity: 0.6 }}>tokens | ${(costData.execution || 0).toFixed(3)}</div>
                  </div>

                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>Validation</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold' }}>{(tokenUsage.validation || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '0.6rem', opacity: 0.6 }}>tokens | ${(costData.validation || 0).toFixed(3)}</div>
                  </div>

                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>Defect Analysis</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold' }}>{(tokenUsage.defect_analysis || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '0.6rem', opacity: 0.6 }}>tokens | ${(costData.defect_analysis || 0).toFixed(3)}</div>
                  </div>

                  {/* Total Tokens - Slightly emphasized */}
                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    gridColumn: 'span 2',
                    border: '2px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>TOTAL TOKENS USED</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold', color: '#000000' }}>{(tokenUsage.total || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '0.6rem', opacity: 0.8, color: '#000000' }}>Total Cost: ${(costData.total || 0).toFixed(3)}</div>
                  </div>

                  {/* Enhanced Mode */}
                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>Enhanced Mode</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold' }}>
                      {currentAgent?.enhanced_mode ? '✓ Yes' : '✗ No'}
                    </div>
                  </div>

                  {/* Test Cases */}
                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>Test Cases</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold' }}>
                      {(() => {
                        // Get count based on current scope
                        if (currentScope === 'api' && selectedAPI) {
                          const api = apis?.find(a => a.name === selectedAPI);
                          return api?.testCount || 0;
                        } else if (currentScope === 'application' && selectedApplication) {
                          const app = applications?.find(a => a.name === selectedApplication);
                          return app?.testCount || 0;
                        } else if (currentScope === 'agent' && selectedAgent) {
                          return currentAgent?.testCaseCount || 0;
                        }
                        return 0;
                      })()}
                    </div>
                  </div>

                  {/* Temperature */}
                  <div style={{
                    background: '#ffffff',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #cccccc',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '0.5rem', opacity: 0.6 }}>Temperature Setting</div>
                    <div style={{ fontSize: '.8rem', fontWeight: 'bold' }}>
                      {aiConfig?.temperature || '0.7'}
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </>
        )}
      </div>
    </>
  );
};

export default AgentDashboardPanel;